import type { ModuleKey } from '@cloud/modules'
import type { NavigationType } from '@/types'

/**
 * Navigation items for ERP modules
 * These are conditionally rendered based on module enablement
 */
export const moduleNavigationsData: NavigationType[] = [
  {
    title: 'CRM',
    items: [
      {
        title: 'Dashboard',
        href: '/crm',
        iconName: 'LayoutDashboard',
        moduleKey: 'crm',
      },
      {
        title: 'Leads',
        href: '/crm/leads',
        iconName: 'UserPlus',
        moduleKey: 'crm',
      },
      {
        title: 'Accounts',
        href: '/crm/accounts',
        iconName: 'Building2',
        moduleKey: 'crm',
      },
      {
        title: 'Contacts',
        href: '/crm/contacts',
        iconName: 'User',
        moduleKey: 'crm',
      },
      {
        title: 'Opportunities',
        href: '/crm/opportunities',
        iconName: 'Target',
        moduleKey: 'crm',
      },
      {
        title: 'Activities',
        href: '/crm/activities',
        iconName: 'CalendarCheck',
        moduleKey: 'crm',
      },
      {
        title: 'Visitors',
        href: '/crm/visitors',
        iconName: 'DoorOpen',
        moduleKey: 'crm',
      },
    ],
  },
  {
    title: 'Human Resources',
    items: [
      {
        title: 'Dashboard',
        href: '/hr',
        iconName: 'LayoutDashboard',
        moduleKey: 'hr',
      },
      {
        title: 'Employees',
        href: '/hr/employees',
        iconName: 'Users',
        moduleKey: 'hr',
      },
      {
        title: 'Departments',
        href: '/hr/departments',
        iconName: 'Building2',
        moduleKey: 'hr',
      },
      {
        title: 'Time Registration',
        href: '/hr/time-registrations',
        iconName: 'CalendarClock',
        moduleKey: 'hr',
      },
      {
        title: 'Shifts',
        href: '/hr/shifts',
        iconName: 'Clock',
        moduleKey: 'hr',
      },
      {
        title: 'Leave',
        href: '/hr/leave',
        iconName: 'CalendarOff',
        moduleKey: 'hr',
      },
      {
        title: 'Leave Balances',
        href: '/hr/leave/balances',
        iconName: 'Wallet',
        moduleKey: 'hr',
      },
      {
        title: 'Onboarding',
        href: '/hr/onboarding',
        iconName: 'UserCheck',
        moduleKey: 'hr',
      },
      {
        title: 'Contracts',
        href: '/hr/contracts',
        iconName: 'FileText',
        moduleKey: 'hr',
      },
      {
        title: 'Documents',
        href: '/hr/documents',
        iconName: 'FolderOpen',
        moduleKey: 'hr',
      },
      {
        title: 'Payroll',
        href: '/hr/payroll',
        iconName: 'Banknote',
        moduleKey: 'hr',
      },
      {
        title: 'Expenses',
        href: '/hr/expense-claims',
        iconName: 'Receipt',
        moduleKey: 'hr',
      },
      {
        title: 'Recruitment',
        href: '/hr/recruitment',
        iconName: 'UserPlus',
        moduleKey: 'hr',
      },
      {
        title: 'Training',
        href: '/hr/training',
        iconName: 'GraduationCap',
        moduleKey: 'hr',
      },
      {
        title: 'Performance',
        href: '/hr/performance-reviews',
        iconName: 'TrendingUp',
        moduleKey: 'hr',
      },
      {
        title: 'Lifecycle',
        href: '/hr/lifecycle',
        iconName: 'GitBranch',
        moduleKey: 'hr',
      },
    ],
  },
  {
    // Eén groep voor het hele handelsproces, in- én verkoop. Het ontwerp
    // `2026-06-24-document-engine-sales-purchasing`, §5.3 schreef twee aparte groepen voor
    // (Verkoop en Inkoop); één gecombineerde groep is een bewuste afwijking daarvan —
    // inkopen en verkopen zijn dezelfde keten en het scheelt een menugroep met één item.
    // Wat wél uit dat ontwerp komt: de inkooporder hoort hier en niet onder Finance.
    title: 'Inkoop & Verkoop',
    items: [
      {
        title: 'Dashboard',
        href: '/sales',
        iconName: 'LayoutDashboard',
        moduleKey: 'sales',
      },
      {
        title: 'Quotes',
        href: '/sales/quotes',
        iconName: 'FilePenLine',
        moduleKey: 'sales',
      },
      // `title` is GEEN label maar een sleutelzaad: de sidebar zoekt
      // `navigation.<titleCaseToCamelCase(title)>` op. Een Nederlandse titel hier levert
      // dus een leeg menu-item op, want die sleutel bestaat niet.
      {
        title: 'Sales orders',
        href: '/sales/orders',
        iconName: 'ClipboardList',
        moduleKey: 'sales',
      },
      {
        title: 'Order lines',
        href: '/sales/order-lines',
        iconName: 'ListChecks',
        moduleKey: 'sales',
      },
      {
        title: 'Purchase orders',
        href: '/purchasing/orders',
        iconName: 'ShoppingCart',
        moduleKey: 'purchasing',
      },
      {
        title: 'Carton to order',
        href: '/purchasing/carton-demand',
        iconName: 'PackageSearch',
        moduleKey: 'purchasing',
      },
      {
        title: 'Coproduction',
        href: '/purchasing/coproduction-demand',
        iconName: 'Handshake',
        moduleKey: 'purchasing',
      },
      {
        title: 'Carton deliveries',
        href: '/purchasing/inbound-deliveries',
        iconName: 'PackageOpen',
        moduleKey: 'purchasing',
      },
    ],
  },
  {
    /*
     * Magazijnwerk onder zijn eigen vlag. De expeditielijst, het scanscherm en de
     * transportzones stonden tot 2026-08-10 onder Verkoop respectievelijk Verpakkingen,
     * met hun rechten op `sales` en `packaging`; die zijn samen met deze menu-ingangen
     * verhuisd naar de logistiek-module.
     *
     * BEZORGWIJZEN staan hier bewust NIET: dat is tenant-stamdata en die hoort onder
     * Instellingen, net als pipelines en palletmaten. Zie `settings-navigation.ts`.
     */
    title: 'Logistics',
    items: [
      {
        title: 'Dashboard',
        href: '/logistics',
        iconName: 'LayoutDashboard',
        moduleKey: 'logistics',
      },
      {
        title: 'Shipments',
        href: '/logistics/shipments',
        iconName: 'Truck',
        moduleKey: 'logistics',
      },
      {
        title: 'Packing slips',
        href: '/logistics/packing-slips',
        iconName: 'FileText',
        moduleKey: 'logistics',
      },
      {
        title: 'Scan pallets',
        href: '/logistics/expeditie/scan',
        iconName: 'ScanLine',
        moduleKey: 'logistics',
      },
      {
        title: 'Receive carton',
        href: '/logistics/inbound/scan',
        iconName: 'PackageCheck',
        moduleKey: 'logistics',
      },
      {
        title: 'Transport zones',
        href: '/logistics/transport-zones',
        iconName: 'MapPin',
        moduleKey: 'logistics',
      },
    ],
  },
  {
    title: 'Finance',
    items: [
      {
        title: 'Dashboard',
        href: '/finance',
        iconName: 'LayoutDashboard',
        moduleKey: 'finance',
      },
      {
        title: 'Invoices',
        href: '/finance/invoices',
        iconName: 'FileText',
        moduleKey: 'finance',
      },
      {
        title: 'Credit notes',
        href: '/finance/credit-notes',
        iconName: 'ReceiptText',
        moduleKey: 'finance',
      },
      {
        title: 'Bills',
        href: '/finance/bills',
        iconName: 'FileText',
        moduleKey: 'finance',
      },
      {
        title: 'Debit notes',
        href: '/finance/debit-notes',
        iconName: 'ReceiptText',
        moduleKey: 'finance',
      },
      {
        title: 'Payments',
        href: '/finance/payments',
        iconName: 'CreditCard',
        moduleKey: 'finance',
      },
      {
        title: 'Recurring',
        href: '/finance/recurring',
        iconName: 'Repeat',
        moduleKey: 'finance',
      },
      {
        title: 'AR aging',
        href: '/finance/reports/ar-aging',
        iconName: 'Activity',
        moduleKey: 'finance',
      },
      {
        title: 'AP aging',
        href: '/finance/reports/ap-aging',
        iconName: 'Activity',
        moduleKey: 'finance',
      },
      {
        title: 'Sales',
        href: '/finance/reports/sales',
        iconName: 'TrendingUp',
        moduleKey: 'finance',
      },
      {
        title: 'Cashflow',
        href: '/finance/reports/cashflow',
        iconName: 'Banknote',
        moduleKey: 'finance',
      },
      {
        title: 'Top customers',
        href: '/finance/reports/top-customers',
        iconName: 'Crown',
        moduleKey: 'finance',
      },
      {
        title: 'Outstanding',
        href: '/finance/reports/outstanding',
        iconName: 'CircleAlert',
        moduleKey: 'finance',
      },
    ],
  },
  {
    title: 'Inventory',
    items: [
      {
        title: 'Dashboard',
        href: '/inventory',
        iconName: 'Package',
        moduleKey: 'inventory',
      },
      {
        title: 'Stock position',
        href: '/inventory/stock',
        iconName: 'Scale',
        moduleKey: 'inventory',
      },
      {
        title: 'Stock valuation',
        href: '/inventory/valuation',
        iconName: 'Coins',
        moduleKey: 'inventory',
      },
      {
        title: 'Lots',
        href: '/inventory/lots',
        iconName: 'Layers',
        moduleKey: 'inventory',
      },
      {
        title: 'Serials',
        href: '/inventory/serials',
        iconName: 'Barcode',
        moduleKey: 'inventory',
      },
      {
        title: 'Stock movements',
        href: '/inventory/stock-movements',
        iconName: 'ArrowLeftRight',
        moduleKey: 'inventory',
      },
      {
        title: 'Adjust',
        href: '/inventory/stock-adjust',
        iconName: 'SlidersHorizontal',
        moduleKey: 'inventory',
      },
      {
        title: 'Transfer',
        href: '/inventory/stock-transfer',
        iconName: 'Send',
        moduleKey: 'inventory',
      },
    ],
  },
  {
    title: 'Products',
    items: [
      {
        title: 'Products',
        href: '/products',
        iconName: 'Tags',
        moduleKey: 'products',
      },
    ],
  },
  {
    title: 'Production',
    items: [
      {
        title: 'Dashboard',
        href: '/production',
        iconName: 'LayoutDashboard',
        moduleKey: 'production',
      },
      {
        title: 'Production orders',
        href: '/production/orders',
        iconName: 'Factory',
        moduleKey: 'production',
      },
      {
        title: 'Machine runs',
        href: '/production/machine-runs',
        iconName: 'Cog',
        moduleKey: 'production',
      },
      {
        title: 'Machine calendar',
        href: '/production/machine-calendar',
        iconName: 'CalendarClock',
        moduleKey: 'production',
      },
      {
        title: 'Machine planning',
        href: '/production/capacity',
        iconName: 'CalendarRange',
        moduleKey: 'production',
      },
      {
        title: 'Maintenance',
        href: '/production/maintenance',
        iconName: 'Wrench',
        moduleKey: 'production',
      },
    ],
  },
  {
    title: 'Packaging',
    items: [
      {
        title: 'Dashboard',
        href: '/packaging',
        iconName: 'Boxes',
        moduleKey: 'packaging',
      },
      {
        title: 'Calculations',
        href: '/packaging/calculations',
        iconName: 'Calculator',
        moduleKey: 'packaging',
      },
      {
        title: 'Box Design',
        href: '/packaging/boxdesign',
        iconName: 'PencilRuler',
        moduleKey: 'packaging',
      },
      {
        title: 'File Viewer',
        href: '/packaging/fileviewer',
        iconName: 'FileSearch',
        moduleKey: 'packaging',
        // Separator below the Calculatie-cluster; the reference/master-data
        // entries below are sorted alphabetically by their Dutch (nl) labels.
        dividerAfter: true,
      },
      {
        // Fefco
        title: 'Box styles',
        href: '/packaging/fefcos',
        iconName: 'Box',
        moduleKey: 'packaging',
      },
      {
        // Kartonkwaliteiten
        title: 'Board qualities',
        href: '/packaging/qualities',
        iconName: 'Layers',
        moduleKey: 'packaging',
      },
      {
        // Kartonleveranciers
        title: 'Supplier paperboard',
        href: '/packaging/suppliers-paperboard',
        iconName: 'Truck',
        moduleKey: 'packaging',
      },
      {
        // Machines
        title: 'Machines',
        href: '/packaging/machines',
        iconName: 'Cog',
        moduleKey: 'packaging',
      },
      {
        // Prijslijsten (spec 2026-10-15-prijslijsten-leverancier §10)
        title: 'Price lists',
        href: '/packaging/price-lists',
        iconName: 'ReceiptText',
        moduleKey: 'packaging',
      },
      {
        // Stockplaten
        title: 'Stock sheets',
        href: '/packaging/stock-sheets',
        iconName: 'Sheet',
        moduleKey: 'packaging',
      },
    ],
  },
  {
    title: 'Projects',
    items: [
      {
        title: 'All Projects',
        href: '/projects',
        iconName: 'FolderKanban',
        moduleKey: 'projects',
      },
      {
        title: 'Tasks',
        href: '/projects/tasks',
        iconName: 'SquareCheck',
        moduleKey: 'projects',
      },
      {
        title: 'Boards',
        href: '/projects/boards',
        iconName: 'Frame',
        moduleKey: 'projects',
      },
      {
        title: 'Design Library',
        href: '/projects/library',
        iconName: 'Library',
        moduleKey: 'projects',
      },
      {
        title: 'Time Tracking',
        href: '/projects/time',
        iconName: 'Timer',
        moduleKey: 'projects',
      },
      {
        title: 'Milestones',
        href: '/projects/milestones',
        iconName: 'Flag',
        moduleKey: 'projects',
      },
      {
        title: 'Polls',
        href: '/projects/polls',
        iconName: 'Vote',
        moduleKey: 'projects',
      },
      {
        title: 'Team',
        href: '/projects/team',
        iconName: 'Users',
        moduleKey: 'projects',
      },
    ],
  },
  // Settings sits last so it stays pinned at the bottom of the sidebar.
  // It is a single entry — the settings area has its own secondary
  // navigation (see settings-navigation.ts + SettingsNav).
  {
    title: 'Settings',
    items: [
      {
        title: 'Settings',
        href: '/settings',
        iconName: 'Settings',
      },
    ],
  },
]

/**
 * Maps navigation item titles to their corresponding module keys
 * Used to determine which module a nav item belongs to
 */
export const navItemModuleMap: Record<string, ModuleKey> = {
  // HR
  Employees: 'hr',
  Leave: 'hr',
  Recruitment: 'hr',
  Payroll: 'hr',

  // Finance
  Dashboard: 'finance',
  Invoices: 'finance',
  Expenses: 'finance',
  Accounts: 'finance',
  Reports: 'finance',

  // Inventory
  Locations: 'inventory',
  Lots: 'inventory',
  Serials: 'inventory',
  'Stock movements': 'inventory',
  Adjust: 'inventory',
  Transfer: 'inventory',

  // CRM
  Contacts: 'crm',
  Companies: 'crm',
  Deals: 'crm',
  Pipeline: 'crm',
  Calls: 'crm',
  Meetings: 'crm',
  Tasks: 'crm',

  // Projects
  'All Projects': 'projects',
  Boards: 'projects',
  'Time Tracking': 'projects',
  Milestones: 'projects',
  Polls: 'projects',
  Team: 'projects',
}
